import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ios-components.css';

/**
 * Bottom Sheet estilo iOS
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - title: string
 * - children: ReactNode
 * - showHandle: boolean (default: true)
 * - height: 'auto' | 'small' | 'medium' | 'large' | 'full'
 */
function IOSSheet({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  showHandle = true,
  height = 'medium'
}) {
  
  // Fecha ao pressionar Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const heightVariants = {
    auto: 'auto',
    small: '30vh',
    medium: '50vh',
    large: '75vh',
    full: '92vh'
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="ios-sheet__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
            onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
          />
          
          {/* Sheet */}
          <motion.div
            className={`ios-sheet ios-sheet--${height}`}
            style={{ '--sheet-height': heightVariants[height] }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ 
              type: 'spring', 
              damping: 25, 
              stiffness: 300 
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, { offset, velocity }) => {
              if (offset.y > 100 || velocity.y > 500) {
                onClose();
              }
            }}
          >
            {showHandle && (
              <div className="ios-sheet__handle-container">
                <div className="ios-sheet__handle" />
              </div>
            )}
            
            {title && (
              <div className="ios-sheet__header">
                <h3 className="ios-sheet__title">{title}</h3>
              </div>
            )}
            
            <div className="ios-sheet__content">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default IOSSheet;
